"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import Image from "next/image";
import { ImagePlus, X, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { StarRating } from "./star-rating";
import { Button } from "@/ui/components/ui/button";
import { defaultLocale, formatDate, isValidLocale, type Locale } from "@/config/locale";
import { REVIEWS_SECTION_ID } from "./review-snippet";

interface ReviewImage {
	id?: string;
	url: string;
	width: number;
	height: number;
}

interface ReviewData {
	id: string;
	rating: number;
	name: string;
	comment: string;
	verified: boolean;
	shopReply?: string | null;
	shopReplyAuthor?: string | null;
	shopReplyCreatedAt?: Date | string | null;
	shopReplyUpdatedAt?: Date | string | null;
	createdAt: Date | string;
	images?: ReviewImage[];
}

const MAX_PHOTOS_PER_REVIEW = 3;
type PhotoUploadStatus = "idle" | "uploading" | "error";

interface ProductReviewsProps {
	productSlug: string;
	initialReviews?: ReviewData[];
}

export function ProductReviews({ productSlug, initialReviews }: ProductReviewsProps) {
	const t = useTranslations("reviews");
	const { locale: localeParam } = useParams<{ locale: string }>();
	const locale: Locale = useMemo(
		() => (localeParam && isValidLocale(localeParam) ? localeParam : defaultLocale),
		[localeParam],
	);
	const [reviews, setReviews] = useState<ReviewData[]>(initialReviews ?? []);
	const [showForm, setShowForm] = useState(false);
	const [formData, setFormData] = useState({ name: "", comment: "", rating: 0 });
	const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
	const [uploadedPhotos, setUploadedPhotos] = useState<ReviewImage[]>([]);
	const [photoUploadStatus, setPhotoUploadStatus] = useState<PhotoUploadStatus>("idle");
	const [photoUploadError, setPhotoUploadError] = useState("");

	const fetchReviews = useCallback(async () => {
		try {
			const res = await fetch(`/api/reviews?productSlug=${encodeURIComponent(productSlug)}`);
			if (res.ok) {
				const data = (await res.json()) as { reviews: ReviewData[] };
				setReviews(data.reviews);
			}
		} catch {
			// Silently fail - reviews are not critical
		}
	}, [productSlug]);

	useEffect(() => {
		if (!initialReviews) {
			// eslint-disable-next-line react-hooks/set-state-in-effect
			fetchReviews();
		}
	}, [initialReviews, fetchReviews]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (formData.rating === 0 || !formData.name.trim() || !formData.comment.trim()) return;

		setStatus("loading");
		try {
			const res = await fetch("/api/reviews", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					productSlug,
					rating: formData.rating,
					name: formData.name,
					comment: formData.comment,
					images: uploadedPhotos,
				}),
			});

			if (res.ok) {
				setFormData({ name: "", comment: "", rating: 0 });
				setUploadedPhotos([]);
				setShowForm(false);
				setStatus("success");
				setTimeout(() => setStatus("idle"), 3000);
			} else {
				setStatus("error");
			}
		} catch {
			setStatus("error");
		}
	};

	async function handlePhotoSelect(e: React.ChangeEvent<HTMLInputElement>) {
		const files = Array.from(e.target.files ?? []);
		e.target.value = ""; // reset so same file can be picked again
		if (!files.length) return;

		const remainingSlots = MAX_PHOTOS_PER_REVIEW - uploadedPhotos.length;
		const toUpload = files.slice(0, remainingSlots);

		setPhotoUploadStatus("uploading");
		setPhotoUploadError("");

		for (const file of toUpload) {
			try {
				const body = new FormData();
				body.append("image", file);
				const res = await fetch("/api/reviews/upload-image", { method: "POST", body });
				if (!res.ok) {
					const data = (await res.json().catch(() => ({}))) as { error?: string };
					setPhotoUploadError(
						data.error === "file_too_large"
							? "Datei zu groß (max 5 MB)"
							: data.error === "mime_not_allowed"
								? "Format nicht erlaubt (nur JPG/PNG/WebP)"
								: "Upload fehlgeschlagen",
					);
					setPhotoUploadStatus("error");
					return;
				}
				const data = (await res.json()) as { url: string; width: number; height: number; sizeBytes: number };
				setUploadedPhotos((prev) => [...prev, data]);
			} catch {
				setPhotoUploadError("Netzwerkfehler beim Upload");
				setPhotoUploadStatus("error");
				return;
			}
		}
		setPhotoUploadStatus("idle");
	}

	function removePhoto(idx: number) {
		setUploadedPhotos((prev) => prev.filter((_, i) => i !== idx));
	}

	const avgRating = reviews.length > 0 ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length : 0;

	return (
		<section id={REVIEWS_SECTION_ID} aria-labelledby="reviews-heading" className="space-y-6">
			{/* Header */}
			<div className="flex flex-wrap items-start justify-between gap-3">
				<div className="space-y-1">
					<h2 id="reviews-heading" className="text-lg font-semibold text-foreground">
						{t("title")}
					</h2>
					{reviews.length > 0 && (
						<div className="flex items-center gap-2">
							<StarRating rating={Math.round(avgRating)} size="sm" />
							<span className="text-sm font-medium tabular-nums text-foreground">{avgRating.toFixed(1)}</span>
							<span className="text-sm text-muted-foreground">
								&middot; {t("basedOn", { count: reviews.length })}
							</span>
						</div>
					)}
				</div>
				<Button variant="outline-solid" size="sm" onClick={() => setShowForm(!showForm)}>
					{t("writeReview")}
				</Button>
			</div>

			{/* Success message */}
			{status === "success" && (
				<div className="flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400">
					<svg className="h-4 w-4 shrink-0" viewBox="0 0 20 20" fill="currentColor">
						<path
							fillRule="evenodd"
							d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
							clipRule="evenodd"
						/>
					</svg>
					{t("pendingModeration")}
				</div>
			)}

			{/* Review Form */}
			{showForm && (
				<form
					onSubmit={handleSubmit}
					className="bg-secondary/30 space-y-4 rounded-xl border border-border p-4 sm:p-5"
				>
					<div>
						<label className="mb-1.5 block text-sm font-medium text-foreground">{t("rating")}</label>
						<StarRating
							rating={formData.rating}
							interactive
							onRate={(r) => setFormData((prev) => ({ ...prev, rating: r }))}
						/>
					</div>
					<div className="grid gap-4 sm:grid-cols-2">
						<div>
							<label className="mb-1.5 block text-sm font-medium text-foreground">{t("name")}</label>
							<input
								type="text"
								value={formData.name}
								onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
								placeholder={t("namePlaceholder")}
								className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
								required
							/>
						</div>
						<div className="sm:col-span-2">
							<label className="mb-1.5 block text-sm font-medium text-foreground">{t("comment")}</label>
							<textarea
								value={formData.comment}
								onChange={(e) => setFormData((prev) => ({ ...prev, comment: e.target.value }))}
								placeholder={t("commentPlaceholder")}
								rows={3}
								className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
								required
							/>
						</div>

						{/* Photo Upload — bis zu 3 Fotos */}
						<div className="sm:col-span-2">
							<label className="mb-1.5 block text-sm font-medium text-foreground">
								Fotos hinzufügen{" "}
								<span className="font-normal text-muted-foreground">
									(optional, bis zu {MAX_PHOTOS_PER_REVIEW})
								</span>
							</label>
							<div className="flex flex-wrap gap-2.5">
								{uploadedPhotos.map((photo, idx) => (
									<div
										key={photo.url}
										className="group relative h-20 w-20 overflow-hidden rounded-lg border border-border bg-secondary"
									>
										<Image
											src={photo.url}
											alt={`Foto ${idx + 1}`}
											fill
											sizes="80px"
											className="object-cover"
										/>
										<button
											type="button"
											onClick={() => removePhoto(idx)}
											aria-label="Foto entfernen"
											className="group-hover:opacity-100 absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity focus:opacity-100"
										>
											<X className="h-3 w-3" />
										</button>
									</div>
								))}
								{uploadedPhotos.length < MAX_PHOTOS_PER_REVIEW && (
									<label className="hover:bg-primary-hero/[0.04] flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border bg-background transition-colors hover:border-primary-hero">
										<input
											type="file"
											accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
											multiple
											onChange={handlePhotoSelect}
											className="sr-only"
											disabled={photoUploadStatus === "uploading"}
										/>
										{photoUploadStatus === "uploading" ? (
											<Loader2 className="h-5 w-5 animate-spin text-primary-hero" />
										) : (
											<>
												<ImagePlus className="h-5 w-5 text-primary-hero" />
												<span className="font-mono text-[0.55rem] uppercase tracking-[0.12em] text-muted-foreground">
													Foto +
												</span>
											</>
										)}
									</label>
								)}
							</div>
							{photoUploadError && <p className="mt-1.5 text-xs text-destructive">{photoUploadError}</p>}
							<p className="mt-1.5 font-mono text-[0.58rem] uppercase tracking-[0.12em] text-muted-foreground">
								JPG · PNG · WebP · max 5 MB · Metadaten werden automatisch entfernt
							</p>
						</div>
					</div>
					<div className="flex items-center gap-3">
						<Button type="submit" size="sm" disabled={status === "loading" || formData.rating === 0}>
							{status === "loading" ? t("submitting") : t("submit")}
						</Button>
						<Button type="button" variant="ghost" size="sm" onClick={() => setShowForm(false)}>
							{t("cancel") ?? "Cancel"}
						</Button>
					</div>
					{status === "error" && <p className="text-sm text-destructive">{t("error")}</p>}
				</form>
			)}

			{/* Review List */}
			{reviews.length > 0 ? (
				<div className="divide-y divide-border">
					{reviews.map((review) => (
						<div key={review.id} className="py-5 first:pt-0 last:pb-0">
							<div className="flex items-start gap-3">
								{/* Avatar */}
								<div className="bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-primary">
									{review.name.charAt(0).toUpperCase()}
								</div>
								<div className="min-w-0 flex-1">
									<div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
										<span className="text-sm font-semibold text-foreground">{review.name}</span>
										{review.verified && (
											<span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400">
												<svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
													<path
														fillRule="evenodd"
														d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
														clipRule="evenodd"
													/>
												</svg>
												{t("verified")}
											</span>
										)}
										<span className="text-xs text-muted-foreground">
											{formatDate(new Date(review.createdAt), undefined, locale)}
										</span>
									</div>
									<div className="mt-1">
										<StarRating rating={review.rating} size="sm" />
									</div>
									<p className="mt-2 text-sm leading-relaxed text-muted-foreground">{review.comment}</p>

									{/* Customer photos — approved only */}
									{review.images && review.images.length > 0 && (
										<div className="mt-3 flex flex-wrap gap-2">
											{review.images.map((img) => (
												<a
													key={img.id || img.url}
													href={img.url}
													target="_blank"
													rel="noreferrer"
													className="group relative block h-24 w-24 overflow-hidden rounded-lg border border-border bg-secondary transition-transform hover:scale-[1.03] hover:shadow-md"
												>
													<Image
														src={img.url}
														alt={`Foto von ${review.name}`}
														fill
														sizes="96px"
														className="object-cover"
													/>
												</a>
											))}
										</div>
									)}

									{review.shopReply && (
										<div className="bg-secondary/40 mt-4 rounded-lg border border-border px-4 py-3">
											<div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
												<p className="text-xs font-semibold uppercase tracking-wide text-foreground">
													{t("shopReplyLabel")}
												</p>
												{review.shopReplyCreatedAt && (
													<span className="text-xs text-muted-foreground">
														{formatDate(new Date(review.shopReplyCreatedAt), undefined, locale)}
													</span>
												)}
											</div>
											<p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
												{review.shopReply}
											</p>
										</div>
									)}
								</div>
							</div>
						</div>
					))}
				</div>
			) : !showForm ? (
				<p className="py-6 text-center text-sm text-muted-foreground">{t("noReviews")}</p>
			) : null}
		</section>
	);
}
